import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import { Card, Row, Col, Statistic, Spin, Empty, Tag } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  WarningOutlined,
  FileOutlined,
  TeamOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { getIssueAnalytics } from "api/issue/issueApi";
import { T, STATUS_COLORS, PRIORITY_COLORS } from "../theme";

// Color schemes come from the shared issue theme (navy/amber/added/removed).
const PIE_COLORS = [T.navy, T.amber, T.added, T.muted, T.removed2];

export default function IssueAnalyticsChart({ projectId = null, compact = false }) {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [projectId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await getIssueAnalytics(projectId);
      setAnalytics(response.data);
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <Empty description={error || "No analytics data available"} />
    );
  }

  // Prepare chart data
  const statusData = Object.entries(analytics.statusDistribution).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    fill: STATUS_COLORS[name] || T.muted,
  }));

  const priorityData = Object.entries(analytics.priorityDistribution).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    fill: PRIORITY_COLORS[name] || T.muted,
  }));

  // Combine issues over time with resolutions
  const timelineData = [];
  const dateMap = {};

  (analytics.issuesOverTime || []).forEach(item => {
    dateMap[item._id] = { date: item._id, created: item.created, resolved: 0 };
  });

  (analytics.resolutionsOverTime || []).forEach(item => {
    if (dateMap[item._id]) {
      dateMap[item._id].resolved = item.resolved;
    } else {
      dateMap[item._id] = { date: item._id, created: 0, resolved: item.resolved };
    }
  });

  Object.values(dateMap).forEach(item => timelineData.push(item));
  timelineData.sort((a, b) => a.date.localeCompare(b.date));

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // By project data
  const projectData = (analytics.byProject || []).map(p => ({
    name: p.projectTitle || "Unknown",
    total: p.total,
    open: p.open,
    resolved: p.resolved,
    urgent: p.urgent,
  }));

  const tickFormatter = (value) => {
    const limit = 10;
    if (!value) return "";
    if (value.length < limit) return value;
    return `${value.substring(0, limit)}...`;
  };

  // Resolution time in hours to readable format
  const formatResolutionTime = (hours) => {
    if (!hours || hours === 0) return "N/A";
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 24) return `${Math.round(hours)} hrs`;
    return `${Math.round(hours / 24)} days`;
  };

  // Compact view for dashboard widget
  if (compact) {
    return (
      <div className="issue-analytics-compact">
        <Row gutter={[16, 16]}>
          {/* Summary Stats */}
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Total Issues"
                value={analytics.summary?.total || 0}
                prefix={<FileOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Open"
                value={analytics.statusDistribution?.open || 0}
                valueStyle={{ color: STATUS_COLORS.open }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Resolved"
                value={analytics.statusDistribution?.resolved || 0}
                valueStyle={{ color: T.added2 }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Urgent"
                value={analytics.priorityDistribution?.urgent || 0}
                valueStyle={{ color: T.removed2 }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Status Distribution Mini Chart */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={12}>
            <Card size="small" title="Status Distribution">
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small" title="Priority Distribution">
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={priorityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Full view
  return (
    <div className="issue-analytics">
      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Total Issues"
              value={analytics.summary?.total || 0}
              prefix={<FileOutlined style={{ color: T.navy }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Open"
              value={analytics.statusDistribution?.open || 0}
              valueStyle={{ color: STATUS_COLORS.open }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Pending"
              value={analytics.statusDistribution?.pending || 0}
              valueStyle={{ color: "#8A6112" }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Resolved"
              value={analytics.statusDistribution?.resolved || 0}
              valueStyle={{ color: T.added2 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Urgent"
              value={analytics.priorityDistribution?.urgent || 0}
              valueStyle={{ color: T.removed2 }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card>
            <Statistic
              title="Avg Resolution"
              value={formatResolutionTime(analytics.resolutionTime?.avgResolutionTime)}
              prefix={<FieldTimeOutlined style={{ color: T.added2 }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Urgent Overdue Alert */}
      {analytics.summary?.urgentOverdue > 0 && (
        <Card 
          style={{ 
            marginBottom: 24, 
            backgroundColor: T.removedBg,
            borderColor: "#FAD6D6" 
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <WarningOutlined style={{ fontSize: 24, color: T.removed2 }} />
            <div>
              <div style={{ fontWeight: 600, color: T.removed2 }}>
                {analytics.summary.urgentOverdue} Urgent Issue{analytics.summary.urgentOverdue > 1 ? "s" : ""} Overdue
              </div>
              <div style={{ fontSize: 12, color: T.removed2 }}>
                These urgent issues have been open for more than 3 days
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Charts Row 1 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Status Distribution Pie Chart */}
        <Col xs={24} md={12}>
          <Card title="Status Distribution" className="chart-card">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Priority Distribution Pie Chart */}
        <Col xs={24} md={12}>
          <Card title="Priority Distribution" className="chart-card">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={priorityData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {priorityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2 - Timeline */}
      {timelineData.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card title="Issues Over Time (Last 30 Days)" className="chart-card">
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis />
                  <Tooltip labelFormatter={(label) => `Date: ${label}`} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="created"
                    name="Created"
                    stackId="1"
                    stroke={T.navy}
                    fill="#D4E3EC"
                  />
                  <Area
                    type="monotone"
                    dataKey="resolved"
                    name="Resolved"
                    stackId="2"
                    stroke={T.added2}
                    fill="#CDEEC1"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      )}

      {/* Charts Row 3 - By Project */}
      {projectData.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card title="Issues by Project" className="chart-card">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={projectData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    tick={{ fontSize: 12 }}
                    tickFormatter={tickFormatter}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="open" name="Open" fill={STATUS_COLORS.open} stackId="a" />
                  <Bar dataKey="resolved" name="Resolved" fill={STATUS_COLORS.resolved} stackId="a" />
                  <Bar dataKey="urgent" name="Urgent" fill={PRIORITY_COLORS.urgent} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      )}

      {/* Top Creators */}
      {(analytics.topCreators || []).length > 0 && (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Top Creators" className="chart-card">
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {analytics.topCreators.map((creator, index) => (
                  <div
                    key={creator._id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      backgroundColor: index === 0 ? T.wash : T.bg,
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: index === 0 ? T.navy : T.sub,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        {index + 1}
                      </span>
                      <span style={{ fontWeight: 500 }}>{creator.name || "Unknown"}</span>
                    </div>
                    <Tag style={{ background: T.wash, borderColor: "#D4E3EC", color: T.navyD }}>{creator.count} issues</Tag>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          {/* Resolution Time Stats */}
          <Col xs={24} md={12}>
            <Card title="Resolution Time Stats" className="chart-card">
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: T.addedBg, borderRadius: 8 }}>
                  <span>Average Resolution Time</span>
                  <span style={{ fontWeight: 600, color: T.added2 }}>
                    {formatResolutionTime(analytics.resolutionTime?.avgResolutionTime)}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: T.wash, borderRadius: 8 }}>
                  <span>Fastest Resolution</span>
                  <span style={{ fontWeight: 600, color: T.navyD }}>
                    {formatResolutionTime(analytics.resolutionTime?.minResolutionTime)}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: T.removedBg, borderRadius: 8 }}>
                  <span>Slowest Resolution</span>
                  <span style={{ fontWeight: 600, color: T.removed2 }}>
                    {formatResolutionTime(analytics.resolutionTime?.maxResolutionTime)}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: T.bg, borderRadius: 8 }}>
                  <span>Total Objects</span>
                  <span style={{ fontWeight: 600 }}>
                    {analytics.summary?.totalObjects || 0}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: T.bg, borderRadius: 8 }}>
                  <span>Total Collaborators</span>
                  <span style={{ fontWeight: 600 }}>
                    {analytics.summary?.totalCollaborators || 0}
                  </span>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}
